import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Divider from 'tio-ui/components/divider';
import Text from 'tio-ui/components/text';
import { PhMagnifyingGlass } from 'ember-phosphor-icons';
import SchoolSelect from 'tio-common/components/tuition-assistance/program-details/school-select';
import { getSingleValueForTasField } from 'tio-data/utils/tuition-assistance/fields';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';
import type { TasUnifiedInstitution } from 'tio-data/data/resources/search.ext';
import type { LpnListing } from 'tio-common/utils/lpn/listing';
import LpnSchoolsDrawer from 'tio-employee/components/lpn/schools-drawer';
import LpnSchoolsGate from 'tio-employee/components/lpn/schools-gate';
import { institutionForLpnIdentifier } from 'tio-employee/utils/lpn/selected-school';

interface TasLpnSchoolPickerSignature {
  Args: {
    /** The program the school is for; scopes both the search and the drawer to its school rules. */
    programTemplate: TasProgramTemplateModel;
    /** The school the dropdown shows; a drawer pick reaches it through the wizard's `onChange`. */
    selected?: TasUnifiedInstitutionModel | TasUnifiedInstitution | null;
    /**
     * Called with the school's search result, whether typed or picked from the drawer, or with
     * nothing when cleared. Not awaited: the drawer closes once the school is found.
     */
    onChange?: (value?: TasUnifiedInstitution) => void;
    /** Classes for the field label, passed through to the school search. */
    labelClass?: string;
    /** Validation messages, shown once the search field has been touched. */
    errors?: string[];
  };
  Element: HTMLDivElement;
}

/**
 * The school field for TA program and application forms: the usual school search, plus a way to
 * browse and pick an LPN school when one is available for the program.
 */
export default class TasLpnSchoolPicker extends Component<TasLpnSchoolPickerSignature> {
  @service declare store: StoreService;

  @tracked isDrawerOpen = false;
  /** Bumped on every open and close, so a lookup that outlives its drawer is dropped. */
  #opening = 0;

  /** This program as the LPN gate reads it: whether it only allows its curated school list. */
  get gateTemplates() {
    const template = this.args.programTemplate;
    const exclusive = getSingleValueForTasField(
      'INSTITUTION_LIST_IS_EXCLUSIVE',
      template.fields,
      false
    );
    return [{ id: template.id, institutionListIsExclusive: Boolean(exclusive) }];
  }

  /** The program rejects unaccredited schools, so the drawer hides them. */
  get accreditedOnly() {
    return !getSingleValueForTasField(
      'ALLOW_UNACCREDITED_INSTITUTIONS',
      this.args.programTemplate.fields,
      false
    );
  }

  openDrawer = () => {
    this.#opening += 1;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.#opening += 1;
    this.isDrawerOpen = false;
  };

  /**
   * Saves a drawer pick under the school's official search record. Resolves false, keeping the
   * drawer open, when the program's school search doesn't return that school. Nothing is saved
   * when the drawer closed while the lookup ran.
   */
  pickSchool = async (listing: LpnListing) => {
    const identifier = listing.page.institutionIdentifier;
    const templateId = this.args.programTemplate.id;
    if (!identifier || !templateId) return false;
    const opening = this.#opening;
    const institution = await institutionForLpnIdentifier(this.store, templateId, identifier);
    if (opening !== this.#opening || !institution) return false;
    this.args.onChange?.(institution);
    return true;
  };

  <template>
    <div>
      <SchoolSelect
        @programTemplate={{@programTemplate}}
        @labelClass={{@labelClass}}
        @selected={{@selected}}
        @onChange={{@onChange}}
        @errors={{@errors}}
        ...attributes
      />
      <LpnSchoolsGate
        @templates={{this.gateTemplates}}
        @accreditedOnly={{this.accreditedOnly}}
        as |identifiers companySettingId|
      >
        <div class="tio-col gap-3 mt-4" data-test-lpn-school-picker-browse>
          <div class="tio-row gap-3">
            <Divider class="flex-1" />
            <span class="tio-text-xs-semibold tio-fg-muted">{{t "lpn.institution_picker.or"}}</span>
            <Divider class="flex-1" />
          </div>
          <Text @tag="p" @role="body-secondary">{{t "lpn.institution_picker.browse_prompt"}}</Text>
          <Button
            class="self-start"
            @intent="primary"
            @appearance="outlined"
            @label={{t "lpn.institution_picker.browse"}}
            @icon={{PhMagnifyingGlass}}
            {{on "click" this.openDrawer}}
            @testHook="tas-school-picker__browse-lpn"
            data-test-lpn-school-picker-browse-button
          />
        </div>
        <LpnSchoolsDrawer
          @isOpen={{this.isDrawerOpen}}
          @onClose={{this.closeDrawer}}
          @companySettingId={{companySettingId}}
          @institutionIdentifiers={{identifiers}}
          @onPick={{this.pickSchool}}
          @accreditedOnly={{this.accreditedOnly}}
        />
      </LpnSchoolsGate>
    </div>
  </template>
}
